import createFetchClient, { type Middleware } from 'openapi-fetch'
import useAuthStore from '../stores/authStore.js'
import { refreshToken } from '@/composables/useAuth.js'
import { app } from '../main.js'
import { formatRFC7231 } from 'date-fns'
import { isObject } from '@vueuse/core'
import type { allPaths } from '@/types/_general'
import {
  type EveItemErrorResponseBody,
  isEveSingleErrorResponseBody,
  type EveListErrorResponseBody,
  isEveListErrorResponseBody,
} from '@/types/_general'

function dateReplacer(this: Record<string, unknown>, key: string, value: unknown) {
  if (this[key] instanceof Date) {
    return formatRFC7231(this[key])
  }
  return value
}

export const apiFetch = createFetchClient<allPaths, 'application/json'>({
  baseUrl: import.meta.env.VITE_APP_API_ROOT,
  bodySerializer: (body: unknown) => (body instanceof FormData ? body : JSON.stringify(body, dateReplacer)),
  querySerializer: (query) => {
    const { where, projection, aggregate, ...params } = query
    const sp = new URLSearchParams(params as Record<string, string>)

    if (where != null) {
      sp.append('where', typeof where === 'string' ? where : JSON.stringify(where, dateReplacer))
    }
    if (projection != null) {
      sp.append('projection', typeof projection === 'string' ? projection : JSON.stringify(projection))
    }
    if (aggregate != null) {
      sp.append('aggregate', typeof aggregate === 'string' ? aggregate : JSON.stringify(aggregate))
    }

    return sp.toString()
  },
})

const cacheMiddleware: Middleware = {
  onRequest({ request }) {
    if (request.headers.get('Cache-Control') == null) {
      request.headers.set('Cache-Control', 'no-cache')
    }
    return request
  },
}
apiFetch.use(cacheMiddleware)

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    return await app.runWithContext(async () => {
      const authStore = useAuthStore()

      if (authStore.selectedToken != null) {
        const exp =
          authStore.selectedToken.payload != null && 'exp' in authStore.selectedToken.payload
            ? authStore.selectedToken.payload.exp
            : null
        if (exp != null && exp * 1000 < Date.now()) {
          await refreshToken()
        }
        request.headers.set('x-api-key', authStore.selectedToken.token)
      }
      return request
    })
  },

  async onResponse({ response, request, options }) {
    if (response.status === 401) {
      const authStore = useAuthStore()

      const newToken = await refreshToken()
      if (newToken) {
        request.headers.set('x-api-key', newToken)
        // Cast to bypass complex type inference on request method
        const requestFn = apiFetch.request as (
          method: string,
          url: string,
          init?: unknown
        ) => Promise<{ response: Response }>
        const retryResult = await requestFn(request.method, request.url, options)
        return retryResult.response
      } else {
        authStore.clearAllTokens()
      }
    }
    return response
  },
}
apiFetch.use(authMiddleware)

const requestFilesMap = new WeakMap<Request, { path: string; file: File }[]>()

function isEmptyObject(obj: unknown): boolean {
  if (!isObject(obj)) return false
  return Object.values(obj as Record<string, unknown>).every((v) => v === undefined || v === null || isEmptyObject(v))
}

function extractFilesAndClone(data: unknown, files: { path: string; file: File }[], path = ''): unknown {
  if (data instanceof File) {
    files.push({ path, file: data })
    return undefined
  } else if (Array.isArray(data)) {
    return data.map((item, index) => extractFilesAndClone(item, files, path ? `${path}.${index}` : `${index}`))
  } else if (isObject(data)) {
    const newObj: Record<string, unknown> = {}
    for (const key in data) {
      if (Object.hasOwn(data, key)) {
        const value = extractFilesAndClone(
          (data as unknown as Record<string, unknown>)[key],
          files,
          path ? `${path}.${key}` : key
        )
        // Skip empty objects (objects with only undefined/null values) - these are left over after file extraction
        if (value !== undefined && !isEmptyObject(value)) {
          newObj[key] = value
        }
      }
    }
    return newObj
  }
  return data
}

const mediaFileMiddleware: Middleware = {
  onRequest({ request, body, options }) {
    if (request.method === 'GET' || request.url.includes('/media--upload/')) return

    if (isObject(body) && !(body instanceof FormData)) {
      const files: { path: string; file: File }[] = []
      const newBody = extractFilesAndClone(body, files)

      if (files.length > 0) {
        const newRequest = new Request(request, {
          body: options.bodySerializer(newBody as Parameters<typeof options.bodySerializer>[0]),
        })
        requestFilesMap.set(newRequest, files)
        return newRequest
      }
    }
  },

  async onResponse({ response, request }) {
    if (!response.ok) return
    const files = requestFilesMap.get(request)
    // eslint-disable-next-line prefer-const
    let [, resource, resourceId] = new URL(request.url).pathname.split('/')
    resourceId ??= (await response.clone().json())._id

    if (files != null && resource != null && resourceId != null) {
      for (const { path, file } of files) {
        const body = new FormData()
        body.append('file', file)
        body.append('dotted_path', path)
        body.append('domain', resource)
        await apiFetch.request('post', '/media--upload/{reference_id}', {
          params: { path: { reference_id: resourceId } },
          // @ts-expect-error FormData _is_ in fact assignable to body!
          body,
        })
      }
      requestFilesMap.delete(request)
    }
  },
}
apiFetch.use(mediaFileMiddleware)

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  statusCode: number
  body: unknown

  constructor(message: string, statusCode: number, body: unknown, options?: ErrorOptions) {
    super(message, options)
    this.statusCode = statusCode
    this.body = body
  }
}

export class EveError extends ApiError {
  issues?: EveItemErrorResponseBody['_issues']
  localeMessage?: string

  constructor(body: EveItemErrorResponseBody, statusCode: number, options?: ErrorOptions) {
    super(
      typeof body._error === 'string' ? body._error : (body._error?.message ?? 'Unknown API Error'),
      statusCode,
      body,
      options
    )
    this.localeMessage = body._locale_message
    this.issues = body._issues
  }
}

export class EveListError extends ApiError {
  itemIssues: Record<number, EveItemErrorResponseBody['_issues']>
  localeMessage?: string

  constructor(body: EveListErrorResponseBody, statusCode: number, options?: ErrorOptions) {
    super(
      typeof body._error === 'string' ? body._error : (body._error?.message ?? 'Unknown API Error'),
      statusCode,
      body,
      options
    )
    this.localeMessage = body._locale_message
    this.itemIssues = {}
    for (const [index, item] of body._items.entries()) {
      if (item._status === 'ERR' && item._issues != null) {
        this.itemIssues[index] = item._issues
      }
    }
  }
}

export function createApiError(body: unknown, statusCode: number): Error {
  if (isEveSingleErrorResponseBody(body)) {
    return new EveError(body, statusCode)
  } else if (isEveListErrorResponseBody(body)) {
    return new EveListError(body, statusCode)
  }
  if (typeof body === 'string') {
    return new ApiError(body, statusCode, body)
  }
  return new ApiError('Unknown API Error', statusCode, body)
}
